import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import LeadTable from './components/LeadTable';
import { Lead } from './types';
import { apiService } from './api';
import { useWebSocket } from './hooks/useWebSocket';

/**
 * REALTIME COLLABORATION FLOW:
 * ============================
 * 
 * LOCAL EDITS (this user):
 * User edits → Optimistic update → API call → WebSocket event → Confirm/rollback
 * 
 * REMOTE EDITS (other users):
 * Other user edits → API call → WebSocket event → This app receives → Auto-update table
 * 
 * RESULT: All users see all changes instantly without page refresh
 */

/**
 * Main App component that manages leads with inline editing and optimistic updates
 */
function App() {
  // State for lead data
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for API connection status
  const [apiConnectionStatus, setApiConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected');

  /**
   * REALTIME EVENT HANDLERS: Process WebSocket events from other users
   */
  const handleLeadCreated = useCallback((newLead: Lead) => {
    // COLLABORATIVE CREATE: Another user created a lead, add to our table
    setLeads(prevLeads => {
      // Check if lead already exists (prevent duplicates)
      const exists = prevLeads.some(lead => lead.id === newLead.id);
      if (exists) return prevLeads;
      
      // Add new lead to the beginning of the list
      return [newLead, ...prevLeads];
    });
    
    console.log('Real-time: Lead created by another user', newLead.name);
  }, []);

  const handleLeadUpdated = useCallback((leadId: string, updatedFields: Partial<Lead>) => {
    // COLLABORATIVE UPDATE: Another user updated a lead → merge changes
    setLeads(prevLeads =>
      prevLeads.map(lead =>
        lead.id === leadId
          ? { ...lead, ...updatedFields } // Merge updated fields
          : lead
      )
    );
    
    console.log('Real-time: Lead updated by another user', leadId, updatedFields);
  }, []);

  const handleWebSocketStatusChange = useCallback((status: string) => {
    console.log('WebSocket status:', status);
  }, []);

  /**
   * WEBSOCKET CONNECTION: Enable real-time collaboration
   * Memoize options to prevent reconnection loops
   */
  const wsOptions = useMemo(() => ({
    onLeadCreated: handleLeadCreated,
    onLeadUpdated: handleLeadUpdated,
    onConnectionStatusChange: handleWebSocketStatusChange
  }), [handleLeadCreated, handleLeadUpdated, handleWebSocketStatusChange]);

  const { 
    isConnected: wsConnected 
  } = useWebSocket(wsOptions);

  /**
   * Fetch leads from the API
   */
  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getLeads();
      setLeads(response.leads);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch leads');
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle updating a lead field with optimistic updates
   */
  const handleUpdateLead = useCallback(async (leadId: string, field: keyof Lead, value: string) => {
    // Store original value for rollback
    const originalLead = leads.find(lead => lead.id === leadId);
    if (!originalLead) {
      throw new Error('Lead not found');
    }

    // Optimistic update - immediately update UI
    setLeads(prevLeads => 
      prevLeads.map(lead => 
        lead.id === leadId 
          ? { ...lead, [field]: value }
          : lead
      )
    );

    try {
      // Send update to backend
      const updatedLead = await apiService.updateLead(leadId, { [field]: value });
      
      // Update with server response (in case server modified the value)
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === leadId ? updatedLead : lead
        )
      );

    } catch (error) {
      // Rollback on error - restore original value
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === leadId ? originalLead : lead
        )
      );
      
      // Re-throw error for EditableCell to handle
      throw error;
    }
  }, [leads]);

  /**
   * Check API health and update connection status
   */
  const checkConnection = async () => {
    try {
      await apiService.healthCheck();
      setApiConnectionStatus('connected');
    } catch (err) {
      setApiConnectionStatus('disconnected');
      console.error('API health check failed:', err);
    }
  };

  // Load leads on component mount
  useEffect(() => {
    const initializeApp = async () => {
      await checkConnection();
      await fetchLeads();
    };
    
    initializeApp();
    
    // Set up periodic health check for API
    const healthCheckInterval = setInterval(checkConnection, 30000); // Check every 30 seconds
    
    return () => {
      clearInterval(healthCheckInterval);
    };
  }, []);

  /**
   * REALTIME STATUS: Combine API and WebSocket connection status
   */
  const getOverallConnectionStatus = () => {
    if (apiConnectionStatus === 'connected' && wsConnected) {
      return 'connected'; // Both API and WebSocket working
    } else if (apiConnectionStatus === 'connected') {
      return 'api-only'; // API works but no real-time
    } else {
      return 'disconnected'; // API down
    }
  };

  const overallStatus = getOverallConnectionStatus();

  /**
   * CREATE NEW LEAD: Add a new lead and see it appear in real-time everywhere
   */
  const handleCreateLead = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const newLead = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      status: (formData.get('status') as string) || 'new'
    };

    try {
      const createdLead = await apiService.createLead(newLead);
      // Add the new lead to the list immediately
      setLeads(prevLeads => [createdLead, ...prevLeads]);
      e.currentTarget.reset(); // Clear form
    } catch (err) {
      console.error('Failed to create lead:', err);
      alert('Failed to create lead. Please try again.');
    }
  };

  return (
    <div className="app">
      <header className="header">
        <h1>CRM Lead Dashboard</h1>
        <div className={`connection-status ${overallStatus}`}>
          {overallStatus === 'connected' && 'Connected (Real-time active)'}
          {overallStatus === 'api-only' && 'Connected (Real-time unavailable)'}
          {overallStatus === 'disconnected' && 'Disconnected from backend'}
        </div>
        <p className="instruction-text">
          Click on any name, email, or status to edit inline
          {wsConnected && ' - Changes sync in real-time across all users'}
        </p>
      </header>

      <main>
        {/* Add Lead Form */}
        <div className="add-lead-section">
          <h2>Add New Lead</h2>
          <form onSubmit={handleCreateLead} className="add-lead-form">
            <input
              type="text"
              name="name"
              placeholder="Name"
              required
              className="form-input"
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              required
              className="form-input"
            />
            <select name="status" className="form-select">
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
            </select>
            <button type="submit" className="btn-submit">
              Add Lead
            </button>
          </form>
        </div>

        <LeadTable 
          leads={leads} 
          loading={loading} 
          error={error}
          onUpdateLead={handleUpdateLead}
        />
      </main>
    </div>
  );
}

export default App;